// frontend/src/components/Booking/BookingDetailNew.tsx
// Complete redesign: Mobile-first, responsive, consolidated booking detail page

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft,
  Calendar,
  MapPin,
  User,
  Building2,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Phone,
  Mail,
  DollarSign,
  MessageSquare,
  Star,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Edit,
  FileText,
  Activity,
  Upload,
  Loader2
} from 'lucide-react';
import { BookingStatus } from '@/types/booking';
import { useBooking, useUpdateBookingStatus } from '@/hooks/useBookings';
import BookingTimeline from './BookingTimeline';
import BookingFollowUps from './BookingFollowUps';

interface BookingDetailProps {
  context?: 'admin' | 'provider' | 'customer';
  onStatusUpdate?: (bookingId: number, status: BookingStatus, notes?: string) => void;
}

const BookingDetailNew: React.FC<BookingDetailProps> = ({
  context = 'customer',
  onStatusUpdate
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const bookingId = parseInt(id || '0');
  
  // State
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'followups'>('overview');
  const [showActions, setShowActions] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic']));

  // Hooks
  const { data: booking, isLoading, error } = useBooking(bookingId);
  const updateStatusMutation = useUpdateBookingStatus();

  // Handle mutation success/error
  useEffect(() => {
    if (updateStatusMutation.isSuccess) {
      toast.success('Booking status updated successfully!');
    }
    if (updateStatusMutation.isError) {
      toast.error(updateStatusMutation.error?.message || 'Failed to update booking status');
    }
  }, [updateStatusMutation.isSuccess, updateStatusMutation.isError, updateStatusMutation.error]);

  // Toggle section expansion
  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Status configuration
  const getStatusConfig = (status: BookingStatus) => {
    const configs = {
      pending: { 
        bg: 'bg-yellow-100', 
        color: 'text-yellow-800', 
        icon: Clock,
        label: 'Pending Confirmation'
      },
      accepted: { 
        bg: 'bg-blue-100', 
        color: 'text-blue-800', 
        icon: CheckCircle,
        label: 'Confirmed'
      },
      completed: { 
        bg: 'bg-green-100', 
        color: 'text-green-800', 
        icon: CheckCircle,
        label: 'Completed'
      },
      cancelled: { 
        bg: 'bg-red-100', 
        color: 'text-red-800', 
        icon: XCircle,
        label: 'Cancelled'
      },
      rejected: { 
        bg: 'bg-red-100', 
        color: 'text-red-800', 
        icon: XCircle,
        label: 'Rejected'
      }
    };
    return configs[status] || configs.pending;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Booking Not Found</h3>
          <p className="text-gray-600 mb-4">The booking you're looking for doesn't exist or you don't have access to it.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(booking.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile-First Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              <span className="hidden sm:inline">Back</span>
            </button>
            
            <div className="flex-1 mx-4">
              <h1 className="text-lg font-semibold text-gray-900 truncate">
                Booking #{bookingId}
              </h1>
              <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color} mt-1`}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusConfig.label}
              </div>
            </div>

            <div className="relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
              
              {showActions && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg border z-10">
                  <div className="py-1">
                    {context !== 'customer' && (
                      <>
                        <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center">
                          <Edit className="h-4 w-4 mr-2" />
                          Update Status
                        </button>
                        <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center">
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Add Note
                        </button>
                      </>
                    )}
                    <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center">
                      <FileText className="h-4 w-4 mr-2" />
                      Export Details
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Info Bar - Mobile Optimized Stacked Layout */}
      <div className="bg-white border-b">
        <div className="px-4 py-3 space-y-3">
          {/* Service Name - First Line */}
          <div className="flex items-center">
            <Package className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900">{booking.service_name || 'Service Request'}</p>
              {booking.service_price && (
                <p className="text-green-600 text-xs font-medium mt-1">{booking.service_price}</p>
              )}
            </div>
          </div>
          
          {/* Date & Time - Second Line */}
          <div className="flex items-center">
            <Calendar className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900">{formatDate(booking.service_date)}</p>
              {booking.preferred_time && (
                <p className="text-gray-600 text-xs mt-1">{booking.preferred_time}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b">
        <div className="px-4">
          <nav className="flex">
            {[
              { key: 'overview', label: 'Overview', icon: FileText },
              { key: 'timeline', label: 'Timeline', icon: Activity },
              { key: 'followups', label: 'Follow-ups', icon: MessageSquare }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key as any)}
                className={`flex-1 py-3 border-b-2 text-xs sm:text-sm font-medium transition-colors ${
                  activeTab === key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {/* Mobile: Icon on top, text below */}
                <div className="flex flex-col items-center sm:flex-row sm:justify-center">
                  <Icon className="h-4 w-4 mb-1 sm:mb-0 sm:mr-2" />
                  <span className="leading-tight">{label}</span>
                </div>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4">
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Basic Information - Always Expanded */}
            <div className="bg-white rounded-lg border">
              <div className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Location</p>
                      <div className="flex items-start mt-1">
                        <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-gray-900">{booking.location}</p>
                      </div>
                    </div>

                    {booking.description && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Description</p>
                        <p className="text-sm text-gray-900 mt-1">{booking.description}</p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    {booking.estimated_duration && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Duration</p>
                        <div className="flex items-center mt-1">
                          <Clock className="h-4 w-4 text-gray-400 mr-2" />
                          <p className="text-sm text-gray-900">{booking.estimated_duration}</p>
                        </div>
                      </div>
                    )}

                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Booking Reference</p>
                      <p className="text-sm font-mono text-gray-900 mt-1">{booking.booking_reference || `BK-${bookingId}`}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="bg-white rounded-lg border">
              <button
                onClick={() => toggleSection('customer')}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50"
              >
                <div className="flex items-center">
                  <User className="h-5 w-5 text-gray-400 mr-3" />
                  <h3 className="font-medium text-gray-900">Customer Information</h3>
                </div>
                {expandedSections.has('customer') ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>
              
              {expandedSections.has('customer') && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Name</p>
                      <p className="text-sm text-gray-900 mt-1">{booking.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</p>
                      <a href={`mailto:${booking.customer_email}`} className="text-sm text-blue-600 hover:text-blue-800 mt-1 flex items-center">
                        <Mail className="h-4 w-4 mr-1" />
                        {booking.customer_email}
                      </a>
                    </div>
                    {booking.customer_phone && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phone</p>
                        <a href={`tel:${booking.customer_phone}`} className="text-sm text-blue-600 hover:text-blue-800 mt-1 flex items-center">
                          <Phone className="h-4 w-4 mr-1" />
                          {booking.customer_phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Provider Information */}
            <div className="bg-white rounded-lg border">
              <button
                onClick={() => toggleSection('provider')}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50"
              >
                <div className="flex items-center">
                  <Building2 className="h-5 w-5 text-gray-400 mr-3" />
                  <h3 className="font-medium text-gray-900">Service Provider</h3>
                </div>
                {expandedSections.has('provider') ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>
              
              {expandedSections.has('provider') && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Business Name</p>
                      <p className="text-sm text-gray-900 mt-1">{booking.business_name}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Contact Person</p>
                      <p className="text-sm text-gray-900 mt-1">{booking.contact_person}</p>
                    </div>
                    {booking.contact_email && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</p>
                        <a href={`mailto:${booking.contact_email}`} className="text-sm text-blue-600 hover:text-blue-800 mt-1 flex items-center">
                          <Mail className="h-4 w-4 mr-1" />
                          {booking.contact_email}
                        </a>
                      </div>
                    )}
                    {booking.contact_phone && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phone</p>
                        <a href={`tel:${booking.contact_phone}`} className="text-sm text-blue-600 hover:text-blue-800 mt-1 flex items-center">
                          <Phone className="h-4 w-4 mr-1" />
                          {booking.contact_phone}
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {(booking.provider_rating || booking.provider_reviews) && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Rating</p>
                      <div className="flex items-center mt-1">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-4 w-4 ${
                                i < Math.floor(booking.provider_rating || 0)
                                  ? 'text-yellow-400 fill-current'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="ml-2 text-sm text-gray-600">
                          {booking.provider_rating?.toFixed(1)} ({booking.provider_reviews} reviews)
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Payment & Pricing */}
            {(booking.service_price || booking.total_amount) && (
              <div className="bg-white rounded-lg border">
                <button
                  onClick={() => toggleSection('payment')}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50"
                >
                  <div className="flex items-center">
                    <DollarSign className="h-5 w-5 text-gray-400 mr-3" />
                    <h3 className="font-medium text-gray-900">Pricing</h3>
                  </div>
                  {expandedSections.has('payment') ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                
                {expandedSections.has('payment') && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="pt-4 space-y-3">
                      {booking.service_price && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Service Price</span>
                          <span className="text-sm font-medium text-gray-900">{booking.service_price}</span>
                        </div>
                      )}
                      {booking.total_amount && (
                        <div className="flex justify-between pt-2 border-t">
                          <span className="text-base font-medium text-gray-900">Total Amount</span>
                          <span className="text-base font-semibold text-green-600">${booking.total_amount}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Attachments */}
            <div className="bg-white rounded-lg border">
              <button
                onClick={() => toggleSection('attachments')}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50"
              >
                <div className="flex items-center">
                  <Upload className="h-5 w-5 text-gray-400 mr-3" />
                  <h3 className="font-medium text-gray-900">Attachments & Media</h3>
                </div>
                {expandedSections.has('attachments') ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>
              
              {expandedSections.has('attachments') && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <div className="pt-4">
                    <p className="text-sm text-gray-600 mb-4">Upload images, documents, or other files related to this booking.</p>
                    
                    {/* Upload Area */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="text-blue-600 hover:text-blue-700 cursor-pointer">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG, PDF up to 10MB</p>
                    </div>
                    
                    {/* Existing Attachments List (when implemented) */}
                    <div className="mt-4">
                      <p className="text-xs text-gray-500 italic">No attachments yet</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notes & Reviews */}
            {(booking.provider_notes || booking.completion_notes || booking.customer_review) && (
              <div className="bg-white rounded-lg border">
                <button
                  onClick={() => toggleSection('notes')}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50"
                >
                  <div className="flex items-center">
                    <MessageSquare className="h-5 w-5 text-gray-400 mr-3" />
                    <h3 className="font-medium text-gray-900">Notes & Reviews</h3>
                  </div>
                  {expandedSections.has('notes') ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                
                {expandedSections.has('notes') && (
                  <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-4">
                    {booking.provider_notes && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Provider Notes</p>
                        <p className="text-sm text-gray-900 mt-1">{booking.provider_notes}</p>
                      </div>
                    )}
                    {booking.completion_notes && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Completion Notes</p>
                        <p className="text-sm text-gray-900 mt-1">{booking.completion_notes}</p>
                      </div>
                    )}
                    {booking.customer_review && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Customer Review</p>
                        <div className="mt-1">
                          {booking.rating && (
                            <div className="flex items-center mb-2">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-4 w-4 ${
                                    i < Math.floor(booking.rating || 0)
                                      ? 'text-yellow-400 fill-current'
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                          )}
                          <p className="text-sm text-gray-900">{booking.customer_review}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Provider Action Buttons - Mobile First Design */}
            {context === 'provider' && booking.status === 'pending' && (
              <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">Action Required</h3>
                    <p className="text-sm text-gray-600">Customer is waiting for your response</p>
                  </div>
                  <AlertCircle className="h-5 w-5 text-blue-500" />
                </div>
                
                {updateStatusMutation.isPending && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600 mr-2" />
                      <span className="text-sm text-blue-700">Updating booking status...</span>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => updateStatusMutation.mutate({
                      bookingId,
                      statusUpdate: { status: 'accepted' }
                    })}
                    disabled={updateStatusMutation.isPending}
                    className="flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {updateStatusMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Accept Booking
                  </button>
                  
                  <button
                    onClick={() => updateStatusMutation.mutate({
                      bookingId,
                      statusUpdate: { status: 'rejected', notes: 'Booking rejected by provider' }
                    })}
                    disabled={updateStatusMutation.isPending}
                    className="flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {updateStatusMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <XCircle className="h-4 w-4 mr-2" />
                    )}
                    Reject Booking
                  </button>
                </div>
              </div>
            )}

            {/* Completed Booking Actions */}
            {context === 'provider' && booking.status === 'accepted' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">Mark as Complete</h3>
                    <p className="text-sm text-gray-600">Service has been completed?</p>
                  </div>
                  <CheckCircle className="h-5 w-5 text-blue-500" />
                </div>
                
                <button
                  onClick={() => updateStatusMutation.mutate({
                    bookingId,
                    statusUpdate: { status: 'completed', notes: 'Service completed successfully' }
                  })}
                  disabled={updateStatusMutation.isPending}
                  className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Completed
                </button>
              </div>
            )}
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div className="bg-white rounded-lg border">
            <BookingTimeline
              bookingId={bookingId}
              userRole={context}
              canAddEntries={context !== 'customer'}
              canEditEntries={context === 'admin'}
              showAddButton={context !== 'customer'}
              className="p-4"
              compact={true}
            />
          </div>
        )}

        {/* Follow-ups Tab */}
        {activeTab === 'followups' && (
          <div className="bg-white rounded-lg border">
            <BookingFollowUps
              bookingId={bookingId}
              userRole={context}
              canManageFollowUps={context !== 'customer'}
              className="p-4"
              compact={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingDetailNew;